import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { getUserInfo } from '../services/auth';
import { changeUserPassword } from '../services/user';
import { useTranslation } from '../i18n/I18nContext';

const ViewProfileLayer = () => {
    const { t } = useTranslation();
    const currentUser = getUserInfo();
    // Users are created from an existing Teacher/Worker record (see
    // CadastrarUtilizadorLayer) and the NUIT/Tax ID lives on that linked
    // record, not on the user itself.
    const nuit = currentUser?.worker?.taxId || currentUser?.teacher?.taxId || currentUser?.nuit || '-';

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    // Toggle function for password field
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Toggle function for confirm password field
    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const resetPasswordFields = () => {
        setNewPassword('');
        setConfirmPassword('');
    };

    // Ported from GlobalHeader/AvatarDropdown.tsx `handleChangePassword`
    // (the "Trocar Senha" header option in sge_infantil_web) — same
    // `/api/user/password/:id` endpoint, called for the logged-in user.
    const handleChangePassword = async () => {
        if (!newPassword || !confirmPassword) {
            toast.warning(t('viewProfile.password.validation.required'));
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error(t('viewProfile.password.validation.mismatch'));
            return;
        }
        if (!currentUser?.id) return;
        setChangingPassword(true);
        try {
            await changeUserPassword(currentUser.id, newPassword, currentUser.id);
            toast.success(t('viewProfile.password.success'));
            resetPasswordFields();
        } catch (err) {
            toast.error(t('viewProfile.password.error'));
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="row gy-4">
            <ToastContainer />
            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                    <div
                        className="w-100"
                        style={{
                            height: 200,
                            background: 'linear-gradient(135deg, var(--primary-600), var(--primary-300))',
                        }}
                    />
                    <div className="pb-24 ms-16 mb-24 me-16  mt--100">
                        <div className="text-center border border-top-0 border-start-0 border-end-0">
                            <Icon
                                icon="bi:person-circle"
                                className="border br-white border-width-2-px w-200-px h-200-px rounded-circle text-secondary-light bg-neutral-100"
                            />
                            <h6 className="mb-0 mt-16">{currentUser?.name || '-'}</h6>
                            <span className="text-secondary-light mb-16">{currentUser?.email || '-'}</span>
                        </div>
                        <div className="mt-24">
                            <h6 className="text-xl mb-16">{t('utilizador.personal.info')}</h6>
                            <ul>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {t('utilizador.field.name')}
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {currentUser?.name || '-'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {t('utilizador.field.email')}
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {currentUser?.email || '-'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {t('utilizador.field.contact')}
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {currentUser?.contact ? `+258 ${currentUser.contact}` : '-'}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {t('global.nuit')}
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {nuit}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-lg-8">
                <div className="card h-100">
                    <div className="card-body p-24">
                        <ul
                            className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                            id="pills-tab"
                            role="tablist"
                        >
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24 active"
                                    id="pills-edit-profile-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-edit-profile"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-edit-profile"
                                    aria-selected="true"
                                >
                                    {t('viewProfile.tab.editProfile')}
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24"
                                    id="pills-change-passwork-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-change-passwork"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-change-passwork"
                                    aria-selected="false"
                                    tabIndex={-1}
                                >
                                    {t('viewProfile.tab.changePassword')}
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24"
                                    id="pills-notification-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-notification"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-notification"
                                    aria-selected="false"
                                    tabIndex={-1}
                                >
                                    {t('viewProfile.tab.notifications')}
                                </button>
                            </li>
                        </ul>
                        <div className="tab-content" id="pills-tabContent">
                            <div
                                className="tab-pane fade show active"
                                id="pills-edit-profile"
                                role="tabpanel"
                                aria-labelledby="pills-edit-profile-tab"
                                tabIndex={0}
                            >
                                <form action="#">
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="name"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    {t('utilizador.field.name')}
                                                    <span className="text-danger-600">*</span>
                                                </label>
                                                <div className="icon-field">
                                                  <span className="icon">
                                                    <Icon icon="f7:person" />
                                                  </span>
                                                  <input
                                                      type="text"
                                                      className="form-control radius-8"
                                                      id="name"
                                                      placeholder="Enter Full Name"
                                                      defaultValue={currentUser?.name || ''}
                                                  />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="email"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    {t('utilizador.field.email')} <span className="text-danger-600">*</span>
                                                </label>
                                                <div className="icon-field">
                                                  <span className="icon">
                                                    <Icon icon="mage:email" />
                                                  </span>
                                                  <input
                                                      type="email"
                                                      className="form-control radius-8"
                                                      id="email"
                                                      placeholder="Enter email address"
                                                      defaultValue={currentUser?.email || ''}
                                                  />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="number"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    {t('utilizador.field.contact')}
                                                </label>
                                                <div className="icon-field">
                                                  <span className="icon">
                                                    <Icon icon="solar:phone-calling-linear" />
                                                  </span>
                                                  <input
                                                      type="text"
                                                      className="form-control radius-8"
                                                      id="number"
                                                      placeholder="Enter phone number"
                                                      defaultValue={currentUser?.contact || ''}
                                                  />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <button
                                            type="button"
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                            <div className="tab-pane fade" id="pills-change-passwork" role="tabpanel" aria-labelledby="pills-change-passwork-tab" tabIndex="0">
                                <div className="mb-20">
                                    <label htmlFor="your-password" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        {t('viewProfile.password.new')} <span className="text-danger-600">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <div className="icon-field">
                                          <span className="icon">
                                            <Icon icon="solar:lock-password-outline" />
                                          </span>
                                          <input
                                              type={passwordVisible ? "text" : "password"}
                                              className="form-control radius-8"
                                              id="your-password"
                                              placeholder={t('viewProfile.password.new.placeholder')}
                                              value={newPassword}
                                              onChange={(e) => setNewPassword(e.target.value)}
                                          />
                                        </div>
                                        <span
                                            className={`toggle-password ${passwordVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                                            onClick={togglePasswordVisibility}
                                        ></span>
                                    </div>
                                </div>

                                <div className="mb-20">
                                    <label htmlFor="confirm-password" className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        {t('viewProfile.password.confirm')} <span className="text-danger-600">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <div className="icon-field">
                                          <span className="icon">
                                            <Icon icon="solar:lock-password-outline" />
                                          </span>
                                          <input
                                              type={confirmPasswordVisible ? "text" : "password"}
                                              className="form-control radius-8"
                                              id="confirm-password"
                                              placeholder={t('viewProfile.password.confirm.placeholder')}
                                              value={confirmPassword}
                                              onChange={(e) => setConfirmPassword(e.target.value)}
                                          />
                                        </div>
                                        <span
                                            className={`toggle-password ${confirmPasswordVisible ? "ri-eye-off-line" : "ri-eye-line"} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                                            onClick={toggleConfirmPasswordVisibility}
                                        ></span>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-center gap-3">
                                    <button
                                        type="button"
                                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                        onClick={resetPasswordFields}
                                        disabled={changingPassword}
                                    >
                                        {t('global.cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                        onClick={handleChangePassword}
                                        disabled={changingPassword}
                                    >
                                        {t('global.save')}
                                    </button>
                                </div>
                            </div>
                            <div
                                className="tab-pane fade"
                                id="pills-notification"
                                role="tabpanel"
                                aria-labelledby="pills-notification-tab"
                                tabIndex={0}
                            >
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                                    <label
                                        htmlFor="companzNew"
                                        className="position-absolute w-100 h-100 start-0 top-0"
                                    />
                                    <div className="d-flex align-items-center gap-3 justify-content-between">
                                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                            Company News
                                        </span>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="companzNew"
                                        />
                                    </div>
                                </div>
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                                    <label
                                        htmlFor="pushNotifcation"
                                        className="position-absolute w-100 h-100 start-0 top-0"
                                    />
                                    <div className="d-flex align-items-center gap-3 justify-content-between">
                                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                            Push Notification
                                        </span>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="pushNotifcation"
                                            defaultChecked=""
                                        />
                                    </div>
                                </div>
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                                    <label
                                        htmlFor="weeklyLetters"
                                        className="position-absolute w-100 h-100 start-0 top-0"
                                    />
                                    <div className="d-flex align-items-center gap-3 justify-content-between">
                                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                            Weekly News Letters
                                        </span>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="weeklyLetters"
                                            defaultChecked=""
                                        />
                                    </div>
                                </div>
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                                    <label
                                        htmlFor="meetUp"
                                        className="position-absolute w-100 h-100 start-0 top-0"
                                    />
                                    <div className="d-flex align-items-center gap-3 justify-content-between">
                                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                            Meetups Near you
                                        </span>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="meetUp"
                                        />
                                    </div>
                                </div>
                                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                                    <label
                                        htmlFor="orderNotification"
                                        className="position-absolute w-100 h-100 start-0 top-0"
                                    />
                                    <div className="d-flex align-items-center gap-3 justify-content-between">
                                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                                            Orders Notifications
                                        </span>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            id="orderNotification"
                                            defaultChecked=""
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default ViewProfileLayer;